import { FC, useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, TextField, SxProps } from '@mui/material';

import { JOB_POSITIONS } from '@carcareers/utils';
import { useSearch, SEARCH_PARAM_KEYWORD } from '@carcareers/contexts';

interface JobSearchKeywordInputProps {
  sx?: SxProps;
}

export const JobSearchKeywordInput: FC<JobSearchKeywordInputProps> = ({ sx }) => {
  const { keyword: keywordContext, updateSearchParams } = useSearch();

  const [keyword, setKeyword] = useState(keywordContext);

  useEffect(() => {
    updateSearchParams(SEARCH_PARAM_KEYWORD, keyword);
  }, [keyword]);

  useEffect(() => {
    if (keywordContext !== keyword) {
      setKeyword(keywordContext);
    }
  }, [keywordContext]);

  return (
    <Autocomplete
      freeSolo
      id="job-search-keyword"
      options={JOB_POSITIONS}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.position)}
      groupBy={(option) => (typeof option === 'string' ? 'Positions' : option.department)}
      onChange={(_, value) => {
        if (value === null || (typeof value === 'string' && value.trim() === '')) {
          setKeyword('');
          updateSearchParams(SEARCH_PARAM_KEYWORD, undefined);
          return;
        }

        if (typeof value === 'string') {
          setKeyword(value);
          updateSearchParams(SEARCH_PARAM_KEYWORD, value);
        } else if (Array.isArray(value)) {
          setKeyword(value.map((option) => (typeof option === 'string' ? option : option.position)).join(', '));
          updateSearchParams(SEARCH_PARAM_KEYWORD, value.map((option) => (typeof option === 'string' ? option : option.position)).join(', '));
        } else {
          setKeyword(value.position);
          updateSearchParams(SEARCH_PARAM_KEYWORD, value.position);
        }
      }}
      onInputChange={(_, newValue, reason) => {
        if (reason === 'clear' || (reason === 'input' && newValue.trim() === '')) {
          setKeyword('');
          updateSearchParams(SEARCH_PARAM_KEYWORD, undefined);
        } else if (reason === 'input') {
          setKeyword(newValue);
          updateSearchParams(SEARCH_PARAM_KEYWORD, newValue);
        }
      }}
      inputValue={keyword}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Search"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        width: '100%',
        display: { xs: 'none', sm: 'flex' },
        my: 1,
        ...sx,
      }}
    />
  );
};

export default JobSearchKeywordInput;
